import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listFaseBestanden,
  uploadFaseBestand,
  deleteFaseBestand,
  downloadFaseBestand,
} from '@/api/faseBestanden';
import { listProjectPlans, deleteProjectPlan } from '@/api/tasks';

interface FaseBestandenProps {
  /** List and download only: no upload, no delete. */
  readOnly?: boolean;
  projectId: number;
  faseId: number;
}

/**
 * Unified row model. Fase-bestanden and project-wide plans live in different S3
 * prefixes with different serve/permission models, so we keep `source` to route
 * download + delete to the right backend, but render both identically.
 */
interface MergedBestand {
  key: string;
  filename: string;
  size: number;
  lastModified: string | null;
  /** 'fase' = per-fase file (authenticated serve); 'plan' = project-wide plan (public serve). */
  source: 'fase' | 'plan';
  /** fase: authenticated serve route; plan: public /photos/serve url. */
  serveUrl: string;
}

/** bytes → human-readable (kB / MB), Dutch decimal comma. */
function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** Plans store the basename as `{ms}-{filename}`; strip the timestamp for display. */
function planDisplayName(key: string): string {
  const raw = key.split('/').pop() || key;
  return raw.replace(/^\d{10,}-/, '');
}

/**
 * Fase-bestanden (Fase 3b) — FASE-wide file attachments on Hetzner S3, served
 * via the AUTHENTICATED stream route (downloads go through apiFetch→blob, never a
 * plain anonymous link). On top of the per-fase files we ALSO merge in the
 * project-wide "plans" (the legacy `{projectName}/plans/` store): those already
 * exist on many projects and should surface in every fase. Plans keep their own
 * public serve + `project:plans:write` delete; per-fase files keep theirs. New
 * uploads from here always go to the per-fase prefix.
 *
 * Upload/delete are gated server-side (`project:files:write` / `project:plans:write`);
 * the dashboard is reachable by admin + projectleider, both of whom hold those
 * permissions, so the buttons always render here.
 */
export function FaseBestanden({ projectId, faseId, readOnly = false }: FaseBestandenProps) {
  const [items, setItems] = useState<MergedBestand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards against a list response from a previously-selected fase landing late.
  const activeFaseId = useRef(faseId);

  const load = useCallback(() => {
    activeFaseId.current = faseId;
    setLoading(true);
    setError(null);
    // Fetch both sources independently — one failing must not blank the other.
    Promise.allSettled([listFaseBestanden(projectId, faseId), listProjectPlans(projectId)])
      .then(([faseRes, plansRes]) => {
        if (activeFaseId.current !== faseId) return;
        const merged: MergedBestand[] = [];
        if (faseRes.status === 'fulfilled') {
          for (const f of faseRes.value?.files ?? []) {
            merged.push({ ...f, source: 'fase' });
          }
        }
        if (plansRes.status === 'fulfilled') {
          for (const p of plansRes.value?.plans ?? []) {
            merged.push({
              key: p.key,
              filename: planDisplayName(p.key),
              size: p.size,
              lastModified: p.lastModified,
              source: 'plan',
              serveUrl: p.url,
            });
          }
        }
        // The per-fase files are the primary content — surface an error if that
        // load fails. A plans-only failure is silent (plans are a supplementary
        // merge), so a missing/failing plans store never blocks the fase files.
        if (faseRes.status === 'rejected') {
          const reason = faseRes.reason;
          setError(reason instanceof Error ? reason.message : 'Bestanden laden mislukt');
        }
        merged.sort((a, b) => (b.lastModified ?? '').localeCompare(a.lastModified ?? ''));
        setItems(merged);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (activeFaseId.current !== faseId) return;
        setError(err instanceof Error ? err.message : 'Bestanden laden mislukt');
        setLoading(false);
      });
  }, [projectId, faseId]);

  useEffect(() => {
    load();
  }, [load]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same filename again still fires onChange.
    e.target.value = '';
    if (!file || readOnly) return;
    setUploading(true);
    setError(null);
    try {
      await uploadFaseBestand(projectId, faseId, file);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const onDownload = async (f: MergedBestand) => {
    // Plans are served publicly (inline) — open directly. Fase files require the
    // authenticated stream, so fetch→blob then trigger a download.
    if (f.source === 'plan') {
      window.open(f.serveUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setBusyKey(f.key);
    try {
      await downloadFaseBestand(f.serveUrl, f.filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download mislukt');
    } finally {
      setBusyKey(null);
    }
  };

  const onDelete = async (f: MergedBestand) => {
    if (readOnly) return;
    const confirmMsg =
      f.source === 'plan'
        ? `Plan "${f.filename}" verwijderen? Dit is een projectbreed plan en verdwijnt uit ÁLLE fases.`
        : `Bestand "${f.filename}" verwijderen? Dit verwijdert het voor de hele fase.`;
    if (!window.confirm(confirmMsg)) return;
    setBusyKey(f.key);
    setError(null);
    try {
      if (f.source === 'plan') {
        await deleteProjectPlan(projectId, f.key);
      } else {
        await deleteFaseBestand(projectId, faseId, f.key);
      }
      setItems((prev) => prev.filter((x) => x.key !== f.key));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Fase-bestanden</h4>
          <p className="text-[11px] text-td-muted">Gedeeld over alle taken in deze fase. Projectbrede plannen verschijnen in elke fase.</p>
        </div>
        {!readOnly && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="shrink-0 rounded-lg bg-td-blue px-3 py-1 text-xs font-semibold text-white transition hover:bg-td-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {uploading ? 'Uploaden…' : '+ Bestand'}
            </button>
            <input ref={inputRef} type="file" className="hidden" onChange={onPick} aria-label="Fase-bestand uploaden" />
          </>
        )}
      </div>

      {error && <p role="alert" className="mb-2 text-[11px] text-td-magenta">{error}</p>}

      {loading ? (
        <p className="text-[11px] text-td-muted">Bestanden laden…</p>
      ) : items.length === 0 ? (
        <p className="text-[11px] text-td-muted">Nog geen bestanden voor deze fase.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((f) => (
            <li
              key={f.key}
              className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-1.5"
            >
              <button
                type="button"
                onClick={() => onDownload(f)}
                disabled={busyKey === f.key}
                className="min-w-0 flex-1 truncate text-left text-sm font-medium text-td-blue hover:underline disabled:opacity-60"
                title={`${f.filename} ${f.source === 'plan' ? 'openen' : 'downloaden'}`}
              >
                {f.filename}
              </button>
              <span className="shrink-0 text-[11px] text-td-muted">{fmtSize(f.size)}</span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => onDelete(f)}
                  disabled={busyKey === f.key}
                  aria-label={`${f.filename} verwijderen`}
                  className="shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold text-td-magenta transition hover:bg-td-magenta/10 disabled:opacity-60"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
